import { useContext } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { TASKS_ENDPOINT, TASKS_QUERY_KEY, TASK_QUERY_KEY, TASK_UNASSIGNED_VALUE } from './constants';
import type {
  TTaskActivity,
  TTaskDashboard,
  TTaskDetails,
  TTaskList,
  TTaskListParams,
  TTaskValues,
  TUpdateTaskDetails,
  TReorderTasks,
  ETaskStatus,
} from './types';

export const useTasks = (params: TTaskListParams) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery<TTaskList>({
    queryKey: [TASKS_QUERY_KEY, params],
    retry,
    placeholderData: (previousData, previousQuery) =>
      (previousQuery?.queryKey[1] as TTaskListParams | undefined)?.project === params.project
        ? keepPreviousData(previousData)
        : undefined,
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<TTaskList>(TASKS_ENDPOINT, {
          signal,
          params: {
            ...params,
            assignee: params.assignee.filter((id) => id !== TASK_UNASSIGNED_VALUE).join(',') || undefined,
            unassigned: params.assignee.includes(TASK_UNASSIGNED_VALUE) ? 'true' : undefined,
            status: params.status.length ? params.status.join(',') : undefined,
          },
        })
      ).data,
  });
  useHandleQueryError(query.isError, query.error, `Loading ${TERMINOLOGY.task.lowerPlural} failed!`, true);
  return query;
};

export const useTask = (id: string, enabled: boolean) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery({
    queryKey: [TASK_QUERY_KEY, id],
    enabled,
    retry,
    queryFn: async ({ signal }) => (await apiClient.get<TTaskDetails>(`${TASKS_ENDPOINT}/${id}`, { signal })).data,
  });
  useHandleQueryError(query.isError, query.error, `Loading the ${TERMINOLOGY.task.lower} failed!`, true);
  return query;
};

export const useProjectDashboard = (project: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery({
    queryKey: [TASKS_QUERY_KEY, { project }, 'dashboard'],
    retry,
    queryFn: async ({ signal }) =>
      (await apiClient.get<TTaskDashboard>(`${TASKS_ENDPOINT}/dashboard`, { signal, params: { project } })).data,
  });
  useHandleQueryError(
    query.isError,
    query.error,
    `Failed to load dashboard for ${TERMINOLOGY.project.lower} ${project}!`,
    `Loading the ${TERMINOLOGY.project.lower} dashboard failed!`
  );
  return query;
};

export const useProjectActivities = (project: string, enabled: boolean) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const query = useQuery({
    queryKey: [TASKS_QUERY_KEY, { project }, 'activities'],
    enabled,
    retry,
    queryFn: async ({ signal }) =>
      (await apiClient.get<TTaskActivity[]>(`${TASKS_ENDPOINT}/activities`, { signal, params: { project } })).data,
  });
  useHandleQueryError(
    query.isError,
    query.error,
    `Failed to load activities for ${TERMINOLOGY.project.lower} ${project}!`,
    'Loading recent activities failed!'
  );
  return query;
};

export const useCreateTask = (project: string) => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: TTaskValues) =>
      (await apiClient.post<string>(TASKS_ENDPOINT, { ...values, project })).data,
    onError: handleApiError(`Creating the ${TERMINOLOGY.task.lower} failed!`, true),
    onSuccess: () => {
      show('success', `${TERMINOLOGY.task.upper} created.`);
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
    },
  });
};

export const useUpdateTask = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, changes }: { id: string; changes: TUpdateTaskDetails }) =>
      (await apiClient.put<TTaskDetails>(`${TASKS_ENDPOINT}/${id}/details`, changes)).data,
    onError: handleApiError(`Updating the ${TERMINOLOGY.task.lower} failed!`, true),
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: [TASK_QUERY_KEY, data.task._id], exact: true });
      queryClient.setQueryData([TASK_QUERY_KEY, data.task._id], data);
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      show('success', `${TERMINOLOGY.task.upper} updated.`);
    },
  });
};

export const useSetTaskStatus = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ETaskStatus }) =>
      (await apiClient.put<TTaskDetails>(`${TASKS_ENDPOINT}/${id}/status`, { status })).data,
    onError: handleApiError(`Changing the ${TERMINOLOGY.task.lower} status failed!`, true),
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: [TASK_QUERY_KEY, data.task._id], exact: true });
      queryClient.setQueryData([TASK_QUERY_KEY, data.task._id], data);
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      show('success', `${TERMINOLOGY.task.upper} status changed.`);
    },
  });
};

export const useArchiveTask = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`${TASKS_ENDPOINT}/${id}`),
    onError: handleApiError(`Archiving the ${TERMINOLOGY.task.lower} failed!`, true),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [TASK_QUERY_KEY, id], exact: true });
      queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY] });
      show('success', `${TERMINOLOGY.task.upper} archived.`);
    },
  });
};

export const useReorderTasks = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: TReorderTasks) => apiClient.put<void>(`${TASKS_ENDPOINT}/order`, values),
    onMutate: async ({ project }) => {
      await queryClient.cancelQueries({ queryKey: [TASKS_QUERY_KEY, { project }] });
    },
    onError: handleApiError(`Reordering ${TERMINOLOGY.task.lowerPlural} failed!`, true),
    // Success and conflicts both require authoritative rows and revision before another drag.
    onSettled: async (_data, _error, { project }) => {
      await queryClient.invalidateQueries({ queryKey: [TASKS_QUERY_KEY, { project }] });
    },
  });
};
